import { useState } from 'react'
import { useEnhancedBrandStore } from '../../stores/enhancedBrandStore'
import { exportBrandsToCSV, generateExportSummary, EXPORT_COLUMNS, DEFAULT_EXPORT_COLUMNS } from '../../utils/csvExport'
import { realAuthService } from '../../services/realAuthService'
import { useAuth } from '../../contexts/AuthContext'

interface ExportModalProps {
  isOpen: boolean
  onClose: () => void
  onExport: (selectedColumns: string[], includeAll: boolean) => void
}

function ExportModal({ isOpen, onClose, onExport }: ExportModalProps) {
  const [selectedColumns, setSelectedColumns] = useState<string[]>([...DEFAULT_EXPORT_COLUMNS])
  const [includeAllColumns, setIncludeAllColumns] = useState(false)

  const toggleColumn = (column: string) => {
    if (selectedColumns.includes(column)) {
      setSelectedColumns(prev => prev.filter(col => col !== column))
    } else {
      setSelectedColumns(prev => [...prev, column])
    }
  }

  const handleSelectAll = () => {
    setSelectedColumns(Object.keys(EXPORT_COLUMNS))
  }

  const handleSelectDefault = () => {
    setSelectedColumns([...DEFAULT_EXPORT_COLUMNS])
  }

  const handleExport = () => {
    onExport(selectedColumns, includeAllColumns)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Export Brand Data
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-96">
          <div className="space-y-6">
            {/* Export Options */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Export Options
              </h3>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={includeAllColumns}
                    onChange={(e) => setIncludeAllColumns(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                    Include all available columns (overrides selection below)
                  </span>
                </label>
              </div>
            </div>

            {/* Column Selection */}
            {!includeAllColumns && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select Columns to Export
                  </h3>
                  <div className="space-x-2">
                    <button
                      onClick={handleSelectDefault}
                      className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300 rounded"
                    >
                      Default
                    </button>
                    <button
                      onClick={handleSelectAll}
                      className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded"
                    >
                      Select All
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded p-3">
                  {Object.entries(EXPORT_COLUMNS).map(([key, label]) => (
                    <label key={key} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(key)}
                        onChange={() => toggleColumn(key)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
                
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {selectedColumns.length} columns selected
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              CSV format with proper escaping for Excel compatibility
            </p>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={!includeAllColumns && selectedColumns.length === 0}
                className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md"
              >
                Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function ExportButton() {
  const { filteredBrands, allBrands } = useEnhancedBrandStore()
  const { hasPermission } = useAuth()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [lastExport, setLastExport] = useState<any>(null)

  // Only show export button for admin users
  if (!hasPermission('export_brands')) {
    return null
  }

  const handleExport = async (selectedColumns: string[], includeAll: boolean) => {
    setIsExporting(true)
    
    try {
      // Use the new API endpoint for CSV export
      const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:8788' : window.location.origin;
      const url = new URL('/api/export', baseUrl);
      
      // Add export parameters
      if (includeAll) {
        url.searchParams.set('includeAllColumns', 'true');
      } else {
        url.searchParams.set('selectedColumns', selectedColumns.join(','));
      }
      
      // TODO: Add current filter parameters to export only filtered data
      // For now, this will export all data
      
      console.log('Exporting from API:', url.toString());
      
      // Get authentication token
      const token = realAuthService.getAccessToken();
      if (!token) {
        throw new Error('No authentication token available for export');
      }
      
      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Export request failed: ${response.status} ${response.statusText}`);
      }
      
      // Get the CSV content
      const csvContent = await response.text();
      
      // Get filename from Content-Disposition header or generate default
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition?.match(/filename="(.+)"/)?.[1] || 
        `brandscope-export-${new Date().toISOString().split('T')[0]}.csv`;
      
      // Create download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
      
      // Calculate export summary
      const lines = csvContent.split('\n').filter(line => line.trim());
      const recordCount = Math.max(0, lines.length - 1); // Subtract header row
      const columnCount = selectedColumns.length;
      
      const summary = generateExportSummary(filteredBrands, allBrands.length);
      
      setLastExport({
        filename,
        recordCount,
        columnCount,
        summary,
        timestamp: new Date().toLocaleString()
      });
      
      console.log('Export completed:', { filename, recordCount, columnCount });
      
    } catch (error) {
      console.error('Export failed:', error);
      
      // Fallback to client-side export if API fails
      console.log('Falling back to client-side export...');
      try {
        const exportResult = exportBrandsToCSV(filteredBrands, {
          includeAllColumns: includeAll,
          selectedColumns,
          filename: `brandscope-filtered-${new Date().toISOString().split('T')[0]}.csv`
        });
        
        const summary = generateExportSummary(filteredBrands, allBrands.length);
        
        setLastExport({
          ...exportResult,
          summary,
          timestamp: new Date().toLocaleString()
        });
        
        console.log('Fallback export completed:', exportResult);
      } catch (fallbackError) {
        console.error('Fallback export also failed:', fallbackError);
      }
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <>
      <div className="flex items-center space-x-3">
        <button
          onClick={() => setIsModalOpen(true)}
          disabled={filteredBrands.length === 0 || isExporting}
          className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          {isExporting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Exporting...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Export</span>
            </>
          )}
        </button>

        {lastExport && (
          <div className="text-xs text-gray-600 dark:text-gray-400">
            Last export: {lastExport.recordCount} brands ({lastExport.timestamp})
          </div>
        )}
      </div>

      <ExportModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onExport={handleExport}
      />
    </>
  )
}